import CallEndRoundedIcon from '@mui/icons-material/CallEndRounded'
import FiberManualRecordRoundedIcon from '@mui/icons-material/FiberManualRecordRounded'
import MicOffRoundedIcon from '@mui/icons-material/MicOffRounded'
import MicRoundedIcon from '@mui/icons-material/MicRounded'
import PauseCircleFilledRoundedIcon from '@mui/icons-material/PauseCircleFilledRounded'
import PhoneInTalkRoundedIcon from '@mui/icons-material/PhoneInTalkRounded'
import PlayCircleFilledRoundedIcon from '@mui/icons-material/PlayCircleFilledRounded'
import SettingsVoiceRoundedIcon from '@mui/icons-material/SettingsVoiceRounded'
import VolumeUpRoundedIcon from '@mui/icons-material/VolumeUpRounded'
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  Slider,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import { memo, useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import { getPortalVoiceOptions } from 'src/mentor/services/portalVoiceCallService'
import {
  endPortalVoiceCall,
  extendPortalVoiceCall,
  getPortalVoiceFriendlyErrorMessage,
  getPortalVoiceCallState,
  refreshPortalVoiceAudioDevices,
  setPortalVoiceCallVolumeBoostPercent,
  setPortalVoiceInputDevice,
  setPortalVoiceOutputDevice,
  startPortalVoiceCall,
  subscribePortalVoiceCallState,
  togglePortalVoiceHold,
  togglePortalVoiceMute,
} from './portalVoiceCallManager'

function formatRemainingTime(seconds) {
  const safeSeconds = Math.max(0, Number(seconds) || 0)
  const hours = Math.floor(safeSeconds / 3600)
  const minutes = Math.floor((safeSeconds % 3600) / 60)
  const secs = safeSeconds % 60

  if (hours > 0) {
    return `${hours}h ${minutes}m`
  }
  if (minutes > 0) {
    return `${minutes}m ${secs}s`
  }
  return `${secs}s`
}

function formatMinutesLabel(seconds) {
  const minutes = Math.max(1, Math.round((Number(seconds) || 0) / 60))
  return `${minutes} minute${minutes === 1 ? '' : 's'}`
}

function formatQuotaUnitsLabel(units) {
  const safeUnits = Math.max(0, Number(units) || 0)
  if (safeUnits <= 0) {
    return '0 min'
  }

  const minutes = Math.round((safeUnits / 60) * 10) / 10
  const formattedMinutes = Number.isInteger(minutes) ? String(minutes) : minutes.toFixed(1)
  return `${formattedMinutes} min`
}

function getAllowanceBucketLabel(bucketType = '') {
  switch (bucketType) {
    case 'support-agent-overall':
      return 'Overall daily allowance'
    case 'support-agent-per-brand':
      return 'Clinic-brand daily allowance'
    case 'support-recipient':
      return 'Support-recipient allowance'
    case 'other-recipient':
      return 'Other-recipient allowance'
    default:
      return 'Daily allowance'
  }
}

function buildBlockedRouteDetails(route = {}) {
  const destinationLabel = route?.destinationLabel || route?.title || 'Participant'
  const allowanceSnapshots = Array.isArray(route?.bundledAllowanceBucketSnapshots)
    ? route.bundledAllowanceBucketSnapshots.filter(Boolean)
    : []

  const allowanceSummary = allowanceSnapshots.length
    ? allowanceSnapshots
        .map((snapshot) => {
          const bucketLabel = getAllowanceBucketLabel(snapshot?.allowanceBucketType)
          return `${bucketLabel}: ${formatQuotaUnitsLabel(snapshot?.availableUnits)} left of ${formatQuotaUnitsLabel(snapshot?.allowanceUnits)} today`
        })
        .join('; ')
    : Number(route?.bundledAllowanceConfiguredUnits) > 0
      ? `${formatQuotaUnitsLabel(route?.bundledAllowanceAvailableUnits)} left of ${formatQuotaUnitsLabel(route?.bundledAllowanceConfiguredUnits)} today`
      : ''

  const quotaScopeType = route?.quotaScopeType || ''
  const quotaScopeLabel = route?.quotaScopeLabel || ''
  const quotaTotalUnits = Number(route?.quotaPurchasedUnits) || 0
  const quotaAvailableUnits = Number(route?.quotaAvailableUnits) || 0
  const quotaSummary =
    quotaScopeType === 'support-agent-profile'
      ? `Global support-agent quota: ${formatQuotaUnitsLabel(quotaAvailableUnits)} available of ${formatQuotaUnitsLabel(quotaTotalUnits)} configured.`
      : quotaScopeLabel || quotaTotalUnits > 0 || quotaAvailableUnits > 0
        ? `Clinic-brand outcall quota: ${formatQuotaUnitsLabel(quotaAvailableUnits)} available of ${formatQuotaUnitsLabel(quotaTotalUnits)} purchased${quotaScopeLabel ? ` for ${quotaScopeLabel}` : ""}.`
        : ''

  const clinicBrandSummary =
    quotaScopeType === 'support-agent-profile'
      ? 'Quota source: Support Agent profile.'
      : quotaScopeLabel
        ? `Purchase via clinic brand: ${quotaScopeLabel}.`
        : ''

  const details = [
    `Participant: ${destinationLabel}.`,
    allowanceSummary ? `Daily allowance: ${allowanceSummary}.` : '',
    quotaSummary,
    clinicBrandSummary,
  ].filter(Boolean)

  return details.length > 1 ? details : []
}
function formatElapsedTime(seconds) {
  const safeSeconds = Math.max(0, Number(seconds) || 0)
  const minutes = Math.floor(safeSeconds / 60)
  const secs = safeSeconds % 60
  return `${minutes}:${String(secs).padStart(2, '0')}`
}

function getProviderBadge(providerType) {
  if (providerType === 'plivo') {
    return '(P)'
  }
  if (providerType === 'twilio') {
    return '(T)'
  }
  return ''
}

function valuesMatchWhenProvided(expectedValue, actualValue) {
  return !expectedValue || expectedValue === actualValue
}

function normalizeAllowedDestinationTypes(allowedDestinationTypes = []) {
  if (!Array.isArray(allowedDestinationTypes)) {
    return []
  }

  return allowedDestinationTypes.filter(Boolean)
}

function routeTypesMatch(allowedDestinationTypes = [], destinationType = '') {
  const normalizedAllowedDestinationTypes = normalizeAllowedDestinationTypes(allowedDestinationTypes)
  if (normalizedAllowedDestinationTypes.length === 0) {
    return true
  }
  return normalizedAllowedDestinationTypes.includes(destinationType)
}

function isPortalVoiceButtonCallOwner({
  actorRole,
  bookedEventId,
  clinicBrandId,
  clinicId,
  practitionerId,
  patientId,
  targetRole,
  allowedDestinationTypes,
  uiInstanceKey,
  callState,
}) {
  if (!callState || actorRole !== callState?.actorRole) {
    return false
  }

  return (
    valuesMatchWhenProvided(bookedEventId, callState?.bookedEventId) &&
    valuesMatchWhenProvided(clinicBrandId, callState?.clinicBrandId) &&
    valuesMatchWhenProvided(clinicId, callState?.clinicId) &&
    valuesMatchWhenProvided(practitionerId, callState?.practitionerId) &&
    valuesMatchWhenProvided(patientId, callState?.patientId) &&
    valuesMatchWhenProvided(targetRole, callState?.targetRole) &&
    routeTypesMatch(allowedDestinationTypes, callState?.destinationType) &&
    (!callState?.uiInstanceKey || uiInstanceKey === callState?.uiInstanceKey)
  )
}

function getCallStatusPresentation(callState = {}) {
  switch (callState?.status) {
    case 'connecting':
      return { label: 'Preparing call', color: 'info' }
    case 'ringing':
      return { label: 'Ringing', color: 'warning' }
    case 'connected':
      return { label: 'Live', color: 'success' }
    case 'reconnecting':
      return { label: 'Reconnecting', color: 'warning' }
    case 'ending':
      return { label: 'Ending', color: 'default' }
    case 'on-hold':
      return { label: 'On Hold', color: 'secondary' }
    case 'ended':
      return { label: 'Ended', color: 'default' }
    default:
      return { label: 'Idle', color: 'default' }
  }
}

function PortalVoiceCallButton({
  actorRole = 'mentor',
  bookedEventId,
  clinicBrandId,
  clinicId,
  practitionerId,
  patientId,
  targetRole,
  eventType,
  eventTypesCsv,
  uiSurface,
  allowedDestinationTypes,
  autoStartWhenSingleRoute = false,
  renderTrigger,
  buttonLabel = 'Call',
  disabled = false,
  iconOnly = false,
  showTooltip = true,
  disableHoverMotion = false,
  size = 'small',
  sx = {},
  rtcConflict = null,
}) {
  const [anchorEl, setAnchorEl] = useState(null)
  const [loadingOptions, setLoadingOptions] = useState(false)
  const [loadingRoute, setLoadingRoute] = useState('')
  const [rtcConflictDialogOpen, setRtcConflictDialogOpen] = useState(false)
  const [resolvingRtcConflict, setResolvingRtcConflict] = useState(false)
  const [optionsState, setOptionsState] = useState({
    routes: [],
    error: '',
  })
  const [callState, setCallState] = useState(getPortalVoiceCallState())
  const [audioPanelOpen, setAudioPanelOpen] = useState(false)
  const [audioActionBusy, setAudioActionBusy] = useState('')
  const [panelDismissed, setPanelDismissed] = useState(false)
  const triggerRef = useRef(null)
  const buttonInstanceKeyRef = useRef(`portal-voice-button-${Math.random().toString(36).slice(2, 10)}`)

  useEffect(() => subscribePortalVoiceCallState(setCallState), [])

  const normalizedAllowedDestinationTypes = useMemo(
    () => normalizeAllowedDestinationTypes(allowedDestinationTypes),
    [allowedDestinationTypes],
  )

  const filterRoutes = (routes = []) => {
    if (normalizedAllowedDestinationTypes.length === 0) {
      return routes
    }
    return routes.filter((route) =>
      normalizedAllowedDestinationTypes.includes(route?.destinationType || route?.id),
    )
  }

  const isCallActive = [
    'connecting',
    'ringing',
    'connected',
    'reconnecting',
    'on-hold',
    'ending',
  ].includes(callState?.status)
  const isBusy = loadingOptions || !!loadingRoute || callState?.status === 'connecting'
  const isRenewing = !!callState?.renewalInFlight
  const hasRtcConflict = !!rtcConflict?.active && !isCallActive
  const callStatusPresentation = getCallStatusPresentation(callState)
  const currentVolumeBoostPercent = Math.max(
    100,
    Math.min(250, Number(callState?.volumeBoostPercent) || 100),
  )
  const inputDevices = Array.isArray(callState?.availableInputDevices)
    ? callState.availableInputDevices
    : []
  const outputDevices = Array.isArray(callState?.availableOutputDevices)
    ? callState.availableOutputDevices
    : []
  const canToggleMute = ['connecting', 'ringing', 'connected', 'reconnecting', 'on-hold'].includes(
    callState?.status,
  )
  const canToggleHold = ['connected', 'on-hold'].includes(callState?.status)
  const callSummaryText = [callState?.destinationLabel, callState?.maskedDestination]
    .filter(Boolean)
    .join(' • ')
  const isCurrentButtonCallOwner = isPortalVoiceButtonCallOwner({
    actorRole,
    bookedEventId,
    clinicBrandId,
    clinicId,
    practitionerId,
    patientId,
    targetRole,
    allowedDestinationTypes: normalizedAllowedDestinationTypes,
    uiInstanceKey: buttonInstanceKeyRef.current,
    callState,
  })

  useEffect(() => {
    if (isCallActive && isCurrentButtonCallOwner && !panelDismissed && !anchorEl) {
      const triggerElement = triggerRef.current
      if (triggerElement) {
        setAnchorEl(triggerElement)
      }
    }

    if (isCallActive && !isCurrentButtonCallOwner && anchorEl) {
      setAnchorEl(null)
    }

    if (!isCallActive) {
      setPanelDismissed(false)
      setAudioPanelOpen(false)
    }
  }, [anchorEl, isCallActive, isCurrentButtonCallOwner, panelDismissed])

  const rtcConflictTitle = rtcConflict?.title || 'End the video call first'
  const rtcConflictDescription =
    rtcConflict?.description ||
    'This phone call needs exclusive microphone access. End your active video call first, then try again.'
  const rtcConflictActionLabel = rtcConflict?.actionLabel || 'End Video Call'
  const rtcConflictSuccessMessage =
    rtcConflict?.successMessage || 'Video call ended. You can place the phone call now.'
  const rtcConflictErrorMessage =
    rtcConflict?.errorMessage || 'Unable to end the active video call right now.'

  const triggerLabel = useMemo(() => {
    if (!isCallActive) {
      return buttonLabel
    }
    if (callState?.status === 'connecting') {
      return 'Connecting...'
    }
    if (callState?.remainingSeconds > 0) {
      return `Ends in ${formatRemainingTime(callState.remainingSeconds)}`
    }
    return 'Call Active'
  }, [buttonLabel, callState?.remainingSeconds, callState?.status, isCallActive])

  const visibleRoutes = useMemo(() => {
    return filterRoutes(optionsState?.routes || [])
  }, [normalizedAllowedDestinationTypes, optionsState?.routes])

  async function loadRoutes() {
    const hasClinicContext = !!(clinicBrandId && clinicId)
    const hasSupportTarget = actorRole === 'support-agent' && !!(patientId || practitionerId)

    if (!bookedEventId && !hasClinicContext && !hasSupportTarget) {
      return {
        routes: [],
        error: '',
      }
    }
    setLoadingOptions(true)
    const result = await getPortalVoiceOptions({
      actorRole,
      bookedEventId,
      clinicBrandId,
      clinicId,
      practitionerId,
      patientId,
      targetRole,
      eventType,
      eventTypesCsv,
    })
    setOptionsState({
      routes: result?.routes || [],
      error: result?.ok ? '' : result?.message || 'Unable to load portal call routes.',
    })
    setLoadingOptions(false)
    return {
      routes: result?.routes || [],
      error: result?.ok ? '' : result?.message || 'Unable to load portal call routes.',
    }
  }

  async function handleTriggerClick(event) {
    event?.preventDefault?.()
    event?.stopPropagation?.()

    if (disabled) {
      return
    }

    if (hasRtcConflict) {
      setRtcConflictDialogOpen(true)
      return
    }

    if (isCallActive) {
      if (isCurrentButtonCallOwner) {
        setAnchorEl(event?.currentTarget || null)
      }
      return
    }

    const triggerElement = triggerRef.current || event?.currentTarget || null
    setAnchorEl(triggerElement)
    const loadedState = await loadRoutes()
    const filteredRoutes = filterRoutes(loadedState?.routes || [])

    if (
      autoStartWhenSingleRoute &&
      filteredRoutes.length === 1 &&
      filteredRoutes[0]?.enabled
    ) {
      await handleRouteSelect(filteredRoutes[0])
      return
    }
  }

  async function handleRouteSelect(route) {
    setLoadingRoute(route?.destinationType || route?.id || '')
    setAnchorEl(null)
    try {
      await startPortalVoiceCall({
        actorRole,
        bookedEventId,
        clinicBrandId,
        clinicId,
        practitionerId,
        patientId,
        targetRole,
        eventType,
        eventTypesCsv,
        destinationType: route?.destinationType || route?.id,
        uiSurface,
        uiInstanceKey: buttonInstanceKeyRef.current,
        providerTypeHint: route?.providerType || null,
      })
    } catch (error) {
      toast.error(
        getPortalVoiceFriendlyErrorMessage(error, 'Unable to place the portal voice call.'),
      )
    } finally {
      setLoadingRoute('')
    }
  }

  async function handleExtendCall() {
    try {
      await extendPortalVoiceCall()
    } catch (error) {
      toast.error(error?.message || 'Unable to extend the portal voice call.')
    }
  }

  function handleEndCall() {
    setPanelDismissed(false)
    setAnchorEl(null)
    endPortalVoiceCall()
  }

  function handleDismissActivePanel() {
    setPanelDismissed(true)
    setAnchorEl(null)
  }

  function handleMenuClose() {
    if (isCallActive) {
      return
    }
    setAnchorEl(null)
  }

  function handleToggleMute() {
    try {
      togglePortalVoiceMute()
    } catch (error) {
      toast.error(getPortalVoiceFriendlyErrorMessage(error, 'Unable to update the mute state.'))
    }
  }

  function handleToggleHold() {
    try {
      togglePortalVoiceHold()
    } catch (error) {
      toast.error(error?.message || 'Unable to update the hold state right now.')
    }
  }

  function handleRecordingAction() {
    if (callState?.recordingEnabled) {
      toast.info('Recording is already active for this call.')
      return
    }

    toast.info('Recording for portal calls follows clinic policy. Manual start will be added separately.')
  }

  async function handleRefreshAudioDevices() {
    setAudioActionBusy('refresh')
    try {
      await refreshPortalVoiceAudioDevices()
    } catch (error) {
      toast.error(
        getPortalVoiceFriendlyErrorMessage(
          error,
          'Unable to load the available microphone and speaker devices.',
        ),
      )
    } finally {
      setAudioActionBusy('')
    }
  }

  async function handleInputDeviceChange(event) {
    const deviceId = event?.target?.value || ''
    if (!deviceId) {
      return
    }

    setAudioActionBusy('input')
    try {
      await setPortalVoiceInputDevice(deviceId)
    } catch (error) {
      toast.error(
        getPortalVoiceFriendlyErrorMessage(error, 'Unable to switch the microphone right now.'),
      )
    } finally {
      setAudioActionBusy('')
    }
  }

  async function handleOutputDeviceChange(event) {
    const deviceId = event?.target?.value || 'default'
    setAudioActionBusy('output')
    try {
      await setPortalVoiceOutputDevice(deviceId)
    } catch (error) {
      toast.error(error?.message || 'Unable to switch the speaker right now.')
    } finally {
      setAudioActionBusy('')
    }
  }

  async function handleResolveRtcConflict() {
    if (typeof rtcConflict?.onResolve !== 'function') {
      setRtcConflictDialogOpen(false)
      return
    }

    setResolvingRtcConflict(true)
    try {
      await rtcConflict.onResolve()
      setRtcConflictDialogOpen(false)
      toast.info(rtcConflictSuccessMessage)
    } catch (error) {
      toast.error(error?.message || rtcConflictErrorMessage)
    } finally {
      setResolvingRtcConflict(false)
    }
  }

  const triggerProps = {
    onClick: handleTriggerClick,
    label: triggerLabel,
    busy: isBusy,
    active: isCallActive,
    disabled,
  }

  const hoverMotionStyles = disableHoverMotion
    ? {}
    : {
        transform: 'translateY(-1px)',
      }

  const iconButtonNode = (
    <IconButton
      ref={triggerRef}
      size={size}
      aria-label={triggerLabel}
      title={showTooltip ? undefined : triggerLabel}
      onClick={handleTriggerClick}
      disabled={disabled}
      sx={{
        width: size === 'large' ? 46 : 40,
        height: size === 'large' ? 46 : 40,
        color: '#ffffff',
        background: isCallActive
          ? 'linear-gradient(135deg, #ef4444 0%, #b91c1c 100%)'
          : 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
        border: '1px solid rgba(255, 255, 255, 0.18)',
        boxShadow: isCallActive
          ? '0 12px 24px rgba(185, 28, 28, 0.28)'
          : '0 12px 24px rgba(37, 99, 235, 0.24)',
        transition: 'box-shadow 140ms ease, filter 140ms ease, background 140ms ease',
        '&:hover': {
          background: isCallActive
            ? 'linear-gradient(135deg, #dc2626 0%, #991b1b 100%)'
            : 'linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)',
          boxShadow: isCallActive
            ? '0 16px 28px rgba(185, 28, 28, 0.34)'
            : '0 16px 28px rgba(37, 99, 235, 0.3)',
          ...hoverMotionStyles,
        },
        ...sx,
      }}
    >
      {isBusy || isRenewing ? (
        <CircularProgress size={16} sx={{ color: 'inherit' }} />
      ) : isCallActive ? (
        <CallEndRoundedIcon fontSize="small" />
      ) : (
        <PhoneInTalkRoundedIcon fontSize="small" />
      )}
    </IconButton>
  )

  const triggerNode = renderTrigger ? (
    renderTrigger(triggerProps)
  ) : iconOnly ? (
    showTooltip ? (
      <Tooltip
        title={triggerLabel}
        arrow
        enterDelay={650}
        enterNextDelay={650}
        disableInteractive
        disableHoverListener={Boolean(anchorEl)}
      >
        {disabled ? <span>{iconButtonNode}</span> : iconButtonNode}
      </Tooltip>
    ) : disabled ? (
      <span>{iconButtonNode}</span>
    ) : (
      iconButtonNode
    )
  ) : (
    <Button
      ref={triggerRef}
      size={size}
      variant="contained"
      color={isCallActive ? 'error' : 'primary'}
      onClick={handleTriggerClick}
      disabled={disabled}
      startIcon={
        isBusy || isRenewing ? (
          <CircularProgress size={14} color="inherit" />
        ) : isCallActive ? (
          <CallEndRoundedIcon />
        ) : (
          <PhoneInTalkRoundedIcon />
        )
      }
      sx={{
        minHeight: 38,
        px: 1.75,
        borderRadius: '999px',
        textTransform: 'none',
        fontWeight: 700,
        letterSpacing: '0.01em',
        color: '#ffffff',
        background: isCallActive
          ? 'linear-gradient(135deg, #ef4444 0%, #b91c1c 100%)'
          : 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
        boxShadow: isCallActive
          ? '0 14px 28px rgba(185, 28, 28, 0.22)'
          : '0 14px 28px rgba(37, 99, 235, 0.2)',
        '&:hover': {
          background: isCallActive
            ? 'linear-gradient(135deg, #dc2626 0%, #991b1b 100%)'
            : 'linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)',
          boxShadow: isCallActive
            ? '0 16px 30px rgba(185, 28, 28, 0.28)'
            : '0 16px 30px rgba(37, 99, 235, 0.26)',
        },
        '&.Mui-disabled': {
          color: 'rgba(255, 255, 255, 0.72)',
          background: 'linear-gradient(135deg, rgba(148, 163, 184, 0.9) 0%, rgba(100, 116, 139, 0.9) 100%)',
        },
        ...sx,
      }}
    >
      {triggerLabel}
    </Button>
  )

  return (
    <>
      {triggerNode}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        MenuListProps={{
          autoFocusItem: false,
          disablePadding: isCallActive,
        }}
        PaperProps={{
          sx: {
            minWidth: isCallActive ? 400 : 300,
            width: isCallActive ? 'min(420px, calc(100vw - 24px))' : 'auto',
            maxWidth: 'calc(100vw - 24px)',
            borderRadius: isCallActive ? '20px' : '14px',
            boxShadow: '0 18px 48px rgba(15, 23, 42, 0.22)',
            overflow: 'hidden',
          },
        }}
      >
        {isCallActive ? (
          <Box sx={{ px: 2, py: 2, width: '100%' }}>
            <Stack spacing={1.5}>
              <Box
                sx={{
                  position: 'relative',
                  overflow: 'hidden',
                  borderRadius: '20px',
                  px: 2,
                  py: 2,
                  color: '#f8fafc',
                  background:
                    callState?.status === 'on-hold'
                      ? 'linear-gradient(145deg, #3f3f46 0%, #18181b 100%)'
                      : 'linear-gradient(145deg, #1d4ed8 0%, #0f172a 100%)',
                  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.08)',
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 'auto -40px -70px auto',
                    width: 180,
                    height: 180,
                    borderRadius: '999px',
                    background: 'rgba(96, 165, 250, 0.18)',
                    filter: 'blur(12px)',
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    inset: '-70px auto auto -60px',
                    width: 160,
                    height: 160,
                    borderRadius: '999px',
                    background: 'rgba(248, 113, 113, 0.14)',
                    filter: 'blur(18px)',
                  }}
                />
                <Stack spacing={1.5} sx={{ position: 'relative', zIndex: 1 }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      <Chip
                        size="small"
                        label={callStatusPresentation.label}
                        sx={{
                          color: '#e2e8f0',
                          borderColor: 'rgba(226, 232, 240, 0.24)',
                          backgroundColor: 'rgba(15, 23, 42, 0.18)',
                          '& .MuiChip-label': { fontWeight: 700 },
                        }}
                        variant="outlined"
                      />
                      {callState?.providerType ? (
                        <Chip
                          size="small"
                          label={`Route ${getProviderBadge(callState.providerType)}`}
                          sx={{
                            color: '#e2e8f0',
                            borderColor: 'rgba(226, 232, 240, 0.24)',
                            backgroundColor: 'rgba(15, 23, 42, 0.18)',
                            '& .MuiChip-label': { fontWeight: 700 },
                          }}
                          variant="outlined"
                        />
                      ) : null}
                    </Stack>
                    <Button
                      size="small"
                      variant="text"
                      onClick={handleDismissActivePanel}
                      sx={{
                        color: 'rgba(241, 245, 249, 0.86)',
                        minWidth: 'unset',
                        px: 1,
                        textTransform: 'none',
                        fontWeight: 700,
                      }}
                    >
                      Hide
                    </Button>
                  </Stack>

                  <Stack spacing={1.5} alignItems="center" textAlign="center">
                    <Stack direction="row" alignItems="flex-end" spacing={0.45} sx={{ height: 34 }}>
                      {[0.45, 0.9, 0.62, 1, 0.58].map((scale, index) => (
                        <Box
                          key={`wave-${index}`}
                          sx={{
                            width: 7,
                            height: `${12 + scale * 24}px`,
                            borderRadius: '999px',
                            background:
                              callState?.status === 'on-hold'
                                ? 'rgba(226, 232, 240, 0.36)'
                                : 'linear-gradient(180deg, rgba(255,255,255,0.95) 0%, rgba(191,219,254,0.42) 100%)',
                            transformOrigin: 'center bottom',
                            animation:
                              callState?.status === 'on-hold'
                                ? 'none'
                                : `portalVoiceWave 1.1s ease-in-out ${index * 0.1}s infinite`,
                            '@keyframes portalVoiceWave': {
                              '0%, 100%': { transform: 'scaleY(0.6)', opacity: 0.55 },
                              '50%': { transform: 'scaleY(1)', opacity: 1 },
                            },
                          }}
                        />
                      ))}
                    </Stack>

                    <Box
                      sx={{
                        width: 88,
                        height: 88,
                        borderRadius: '999px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '1px solid rgba(255,255,255,0.16)',
                        background:
                          callState?.status === 'on-hold'
                            ? 'rgba(63, 63, 70, 0.56)'
                            : 'radial-gradient(circle at 30% 20%, rgba(255,255,255,0.18), rgba(37,99,235,0.08) 60%), rgba(15,23,42,0.4)',
                        boxShadow:
                          callState?.status === 'on-hold'
                            ? '0 0 0 10px rgba(255,255,255,0.04)'
                            : '0 0 0 10px rgba(255,255,255,0.05), 0 16px 32px rgba(15,23,42,0.28)',
                      }}
                    >
                      <PhoneInTalkRoundedIcon sx={{ fontSize: 36, color: '#ffffff' }} />
                    </Box>

                    <Stack spacing={0.4}>
                      <Typography variant="h6" sx={{ fontWeight: 800, letterSpacing: '-0.01em' }}>
                        {callState?.destinationLabel || 'Connected participant'}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'rgba(226, 232, 240, 0.78)' }}>
                        {callSummaryText || 'Phone call in progress'}
                      </Typography>
                      {callState?.outboundCallerNumber || callState?.outboundCallerNumberMasked ? (
                        <Typography variant="caption" sx={{ color: 'rgba(226, 232, 240, 0.72)' }}>
                          Calling from{' '}
                          {callState?.outboundCallerNumber || callState?.outboundCallerNumberMasked}
                          {callState?.providerType ? ` ${getProviderBadge(callState.providerType)}` : ''}
                        </Typography>
                      ) : null}
                    </Stack>
                  </Stack>

                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap justifyContent="center">
                    <Chip
                      size="small"
                      label={
                        callState?.remainingSeconds > 0
                          ? `Ends in ${formatRemainingTime(callState.remainingSeconds)}`
                          : 'Ending soon'
                      }
                      sx={{
                        color: '#f8fafc',
                        backgroundColor: 'rgba(15, 23, 42, 0.22)',
                        '& .MuiChip-label': { fontWeight: 700 },
                      }}
                    />
                    {callState?.connectedAtMs ? (
                      <Chip
                        size="small"
                        label={`Live ${formatElapsedTime(callState.elapsedSeconds)}`}
                        sx={{
                          color: '#f8fafc',
                          backgroundColor: 'rgba(15, 23, 42, 0.22)',
                          '& .MuiChip-label': { fontWeight: 700 },
                        }}
                      />
                    ) : null}
                    <Chip
                      size="small"
                      label={callState?.muted ? 'Muted' : 'Mic live'}
                      sx={{
                        color: '#f8fafc',
                        backgroundColor: 'rgba(15, 23, 42, 0.22)',
                        '& .MuiChip-label': { fontWeight: 700 },
                      }}
                    />
                    <Chip
                      size="small"
                      label={callState?.holdActive ? 'Held locally' : 'Live audio'}
                      sx={{
                        color: '#f8fafc',
                        backgroundColor: 'rgba(15, 23, 42, 0.22)',
                        '& .MuiChip-label': { fontWeight: 700 },
                      }}
                    />
                    {callState?.recordingEnabled ? (
                      <Chip
                        size="small"
                        icon={<FiberManualRecordRoundedIcon sx={{ color: '#fecaca !important' }} />}
                        label="Recording"
                        sx={{
                          color: '#fee2e2',
                          backgroundColor: 'rgba(153, 27, 27, 0.24)',
                          '& .MuiChip-icon': {
                            animation: 'portalVoiceRecordPulse 1s ease-in-out infinite',
                          },
                          '@keyframes portalVoiceRecordPulse': {
                            '0%, 100%': { opacity: 0.45, transform: 'scale(0.88)' },
                            '50%': { opacity: 1, transform: 'scale(1)' },
                          },
                        }}
                      />
                    ) : null}
                  </Stack>
                </Stack>
              </Box>

              {callState?.providerFallbackUsed ? (
                <Box
                  sx={{
                    px: 1.25,
                    py: 1,
                    borderRadius: '14px',
                    border: '1px solid rgba(245, 158, 11, 0.28)',
                    backgroundColor: 'rgba(245, 158, 11, 0.08)',
                  }}
                >
                  <Typography variant="caption" sx={{ display: 'block', fontWeight: 800, color: 'warning.dark' }}>
                    Preferred route shifted to {getProviderBadge(callState?.providerType)} during call bootstrap.
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                    {callState?.providerBootstrapFallbackReason ||
                      'The preferred calling service could not be prepared, so the fallback provider was used.'}
                  </Typography>
                </Box>
              ) : null}

              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Button
                  size="small"
                  variant={callState?.muted ? 'contained' : 'outlined'}
                  color={callState?.muted ? 'warning' : 'inherit'}
                  startIcon={callState?.muted ? <MicOffRoundedIcon /> : <MicRoundedIcon />}
                  onClick={handleToggleMute}
                  disabled={!canToggleMute || !!audioActionBusy || callState?.holdActive}
                  sx={{ borderRadius: '999px', textTransform: 'none', fontWeight: 700 }}
                >
                  {callState?.muted ? 'Unmute' : 'Mute'}
                </Button>
                <Button
                  size="small"
                  variant={callState?.holdActive ? 'contained' : 'outlined'}
                  color={callState?.holdActive ? 'secondary' : 'inherit'}
                  startIcon={
                    callState?.holdActive ? <PlayCircleFilledRoundedIcon /> : <PauseCircleFilledRoundedIcon />
                  }
                  onClick={handleToggleHold}
                  disabled={!canToggleHold || !!audioActionBusy}
                  sx={{ borderRadius: '999px', textTransform: 'none', fontWeight: 700 }}
                >
                  {callState?.holdActive ? 'Resume' : 'Hold'}
                </Button>
                <Button
                  size="small"
                  variant={audioPanelOpen ? 'contained' : 'outlined'}
                  color={audioPanelOpen ? 'primary' : 'inherit'}
                  startIcon={<SettingsVoiceRoundedIcon />}
                  onClick={() => {
                    setAudioPanelOpen((currentValue) => !currentValue)
                    if (!audioPanelOpen && !inputDevices.length && !outputDevices.length) {
                      void handleRefreshAudioDevices()
                    }
                  }}
                  disabled={!!audioActionBusy}
                  sx={{ borderRadius: '999px', textTransform: 'none', fontWeight: 700 }}
                >
                  Audio
                </Button>
                <Button
                  size="small"
                  variant={callState?.recordingEnabled ? 'contained' : 'outlined'}
                  color={callState?.recordingEnabled ? 'error' : 'inherit'}
                  startIcon={<FiberManualRecordRoundedIcon />}
                  onClick={handleRecordingAction}
                  sx={{ borderRadius: '999px', textTransform: 'none', fontWeight: 700 }}
                >
                  {callState?.recordingEnabled ? 'Recording' : 'Record'}
                </Button>
              </Stack>

              <Collapse in={audioPanelOpen} timeout="auto" unmountOnExit>
                <Box
                  sx={{
                    px: 1.5,
                    py: 1.4,
                    borderRadius: '16px',
                    border: '1px solid rgba(37, 99, 235, 0.14)',
                    backgroundColor: 'rgba(37, 99, 235, 0.04)',
                  }}
                >
                  <Stack spacing={1.25}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Stack direction="row" spacing={0.75} alignItems="center">
                        <VolumeUpRoundedIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                          Audio Controls
                        </Typography>
                      </Stack>
                      <Button
                        size="small"
                        variant="text"
                        onClick={handleRefreshAudioDevices}
                        disabled={audioActionBusy === 'refresh'}
                        sx={{ textTransform: 'none', fontWeight: 700 }}
                      >
                        {audioActionBusy === 'refresh' ? 'Refreshing...' : 'Refresh'}
                      </Button>
                    </Stack>

                    <Stack spacing={0.75}>
                      <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.primary' }}>
                          Audio Boost
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {currentVolumeBoostPercent}%
                        </Typography>
                      </Stack>
                      <Slider
                        size="small"
                        min={100}
                        max={250}
                        step={10}
                        value={currentVolumeBoostPercent}
                        onChange={(_, value) =>
                          setPortalVoiceCallVolumeBoostPercent(
                            Array.isArray(value) ? value[0] : value,
                          )
                        }
                        aria-label="Portal voice audio boost"
                        valueLabelDisplay="auto"
                      />
                      <Typography variant="caption" color="text.secondary">
                        Boost the incoming call audio if the call feels quiet.
                      </Typography>
                    </Stack>

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                      <Stack spacing={0.6} sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.primary' }}>
                          Microphone
                        </Typography>
                        <Box
                          component="select"
                          value={callState?.selectedInputDeviceId || ''}
                          onChange={handleInputDeviceChange}
                          disabled={!inputDevices.length || audioActionBusy === 'input'}
                          sx={{
                            width: '100%',
                            borderRadius: '12px',
                            border: '1px solid rgba(148, 163, 184, 0.42)',
                            backgroundColor: '#ffffff',
                            color: 'text.primary',
                            fontSize: 14,
                            px: 1.2,
                            py: 1,
                            outline: 'none',
                          }}
                        >
                          {inputDevices.length ? (
                            inputDevices.map((device) => (
                              <option key={device?.deviceId || 'mic-default'} value={device?.deviceId || ''}>
                                {device?.label || 'Microphone'}
                              </option>
                            ))
                          ) : (
                            <option value="">No microphones found</option>
                          )}
                        </Box>
                      </Stack>

                      <Stack spacing={0.6} sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.primary' }}>
                          Speaker
                        </Typography>
                        <Box
                          component="select"
                          value={callState?.selectedOutputDeviceId || 'default'}
                          onChange={handleOutputDeviceChange}
                          disabled={
                            !outputDevices.length ||
                            audioActionBusy === 'output' ||
                            !callState?.outputDeviceSelectionSupported
                          }
                          sx={{
                            width: '100%',
                            borderRadius: '12px',
                            border: '1px solid rgba(148, 163, 184, 0.42)',
                            backgroundColor: '#ffffff',
                            color: 'text.primary',
                            fontSize: 14,
                            px: 1.2,
                            py: 1,
                            outline: 'none',
                          }}
                        >
                          {outputDevices.length ? (
                            outputDevices.map((device) => (
                              <option key={device?.deviceId || 'speaker-default'} value={device?.deviceId || 'default'}>
                                {device?.label || 'Speaker'}
                              </option>
                            ))
                          ) : (
                            <option value="default">System default speaker</option>
                          )}
                        </Box>
                      </Stack>
                    </Stack>

                    {audioActionBusy && audioActionBusy !== 'refresh' ? (
                      <Typography variant="caption" color="text.secondary">
                        Updating {audioActionBusy === 'input' ? 'microphone' : 'speaker'}...
                      </Typography>
                    ) : null}
                    {callState?.audioDeviceError ? (
                      <Typography variant="caption" color="error.main">
                        {callState.audioDeviceError}
                      </Typography>
                    ) : null}
                    {!callState?.outputDeviceSelectionSupported ? (
                      <Typography variant="caption" color="text.secondary">
                        Speaker switching depends on browser support for routed audio output devices.
                      </Typography>
                    ) : null}
                  </Stack>
                </Box>
              </Collapse>

              {callState?.error ? (
                <Typography variant="body2" color="error.main">
                  {callState.error}
                </Typography>
              ) : null}

              <Divider />

              <Stack direction="row" spacing={1.25} justifyContent="space-between" alignItems="center">
                {callState?.renewalEnabled ? (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={handleExtendCall}
                    disabled={!callState?.renewalAllowed || isRenewing}
                    sx={{ borderRadius: '999px', textTransform: 'none', fontWeight: 700 }}
                  >
                    {isRenewing
                      ? 'Extending...'
                      : `Extend by ${formatMinutesLabel(callState?.renewalIncrementSeconds)}`}
                  </Button>
                ) : (
                  <Box />
                )}
                <Button
                  size="small"
                  color="error"
                  variant="contained"
                  onClick={handleEndCall}
                  startIcon={<CallEndRoundedIcon />}
                  sx={{ borderRadius: '999px', textTransform: 'none', fontWeight: 800 }}
                >
                  End Call
                </Button>
              </Stack>
            </Stack>
          </Box>
        ) : loadingOptions ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.5 }}>
            <CircularProgress size={18} />
            <Typography variant="body2">Loading call routes...</Typography>
          </Box>
        ) : optionsState?.error ? (
          <Box sx={{ px: 2, py: 1.5, maxWidth: 320 }}>
            <Typography variant="body2" color="error.main">
              {optionsState.error}
            </Typography>
          </Box>
        ) : visibleRoutes.length > 0 ? (
          visibleRoutes.map((route) => {
            const destinationType = route?.destinationType || route?.id
            const blockedRouteDetails = !route?.enabled ? buildBlockedRouteDetails(route) : []
            const secondaryText = route?.enabled
              ? [
                  getProviderBadge(route?.providerType),
                  route?.destinationLabel,
                  route?.maskedDestination,
                ]
                  .filter(Boolean)
                  .join(' • ')
              : blockedRouteDetails.length > 0
                ? (
                    <Stack spacing={0.25} sx={{ mt: 0.25 }}>
                      {blockedRouteDetails.map((detail) => (
                        <Typography
                          key={detail}
                          variant="caption"
                          component="div"
                          sx={{ color: 'warning.dark', lineHeight: 1.4 }}
                        >
                          {detail}
                        </Typography>
                      ))}
                    </Stack>
                  )
                : route?.disabledReason || 'This route is currently unavailable.'
            const primaryText = !route?.enabled && route?.destinationLabel && route?.title
              ? `${route.title} • ${route.destinationLabel}`
              : route?.title || route?.destinationLabel || destinationType

            return (
              <MenuItem
                key={destinationType}
                onClick={() => route?.enabled && handleRouteSelect(route)}
                disabled={!route?.enabled || !!loadingRoute}
                sx={{
                  alignItems: 'flex-start',
                  py: 1.5,
                  whiteSpace: 'normal',
                }}
              >
                <ListItemText
                  primary={primaryText}
                  secondary={secondaryText}
                  primaryTypographyProps={{ fontWeight: 600, component: 'div' }}
                  secondaryTypographyProps={{
                    component: blockedRouteDetails.length > 0 ? 'div' : 'p',
                    sx: {
                      color: route?.enabled ? 'text.secondary' : 'warning.dark',
                      mt: 0.25,
                    },
                  }}
                />
              </MenuItem>
            )
          })
        ) : (
          <Box sx={{ px: 2, py: 1.5, maxWidth: 320 }}>
            <Typography variant="body2" color="text.secondary">
              No portal call routes are available here right now.
            </Typography>
          </Box>
        )}
      </Menu>
      <Dialog
        open={rtcConflictDialogOpen}
        onClose={() => !resolvingRtcConflict && setRtcConflictDialogOpen(false)}
        PaperProps={{
          sx: {
            maxWidth: 420,
            width: 'calc(100% - 32px)',
            borderRadius: '18px',
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Stack spacing={1}>
            <Chip
              size="small"
              color="warning"
              variant="outlined"
              label={rtcConflict?.badgeLabel || 'Video Call Active'}
              sx={{ alignSelf: 'flex-start', fontWeight: 700 }}
            />
            <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
              {rtcConflictTitle}
            </Typography>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ pt: '4px !important' }}>
          <Stack spacing={1.5}>
            <Typography variant="body2" color="text.secondary">
              {rtcConflictDescription}
            </Typography>
            {rtcConflict?.helperText ? (
              <Box
                sx={{
                  px: 1.5,
                  py: 1.25,
                  borderRadius: '12px',
                  background: 'rgba(37, 99, 235, 0.06)',
                  border: '1px solid rgba(37, 99, 235, 0.12)',
                }}
              >
                <Typography variant="body2" sx={{ color: 'primary.dark', fontWeight: 600 }}>
                  {rtcConflict.helperText}
                </Typography>
              </Box>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 0 }}>
          <Button
            variant="text"
            onClick={() => setRtcConflictDialogOpen(false)}
            disabled={resolvingRtcConflict}
          >
            Not now
          </Button>
          {typeof rtcConflict?.onResolve === 'function' ? (
            <Button
              variant="contained"
              color="error"
              onClick={handleResolveRtcConflict}
              disabled={resolvingRtcConflict}
            >
              {resolvingRtcConflict ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                rtcConflictActionLabel
              )}
            </Button>
          ) : null}
        </DialogActions>
      </Dialog>
    </>
  )
}

export default memo(PortalVoiceCallButton)
